// 3_pages/ordering_address_pl.js
// Natima.pl checkout (body.id--17): light-touch address checks that work with
// Foxentry autocomplete instead of fighting it.
//
// Our rules on submit (billing + optional delivery):
// - ZIP must contain exactly 5 digits (we never insert a hyphen)
// - street must contain a house number (at least one digit)
// - clear stale Shoptet "required" errors when a field already has a value
//
// Foxentry owns green/red ticks. Address is one validator group
// (street+number, city, ZIP). After a user edits one field we re-nudge
// Foxentry in that order — never all at once — so green checks can return.

import { getShopKey } from "../0_global/shop_map.js";

const ZIP_MSG = "checkoutZipDigitsOnly";
const STREET_MSG = "checkoutStreetHouseNumber";
const ZIP_ERROR_TYPE = "natimaPlZip";
const STREET_ERROR_TYPE = "natimaPlStreet";
const FOXENTRY_STEP_MS = 400;

function isPlCheckout() {
	if (getShopKey() === "pl") return true;
	const langInput = document.querySelector('#order-form input[name="language"]');
	return Boolean(langInput && langInput.value === "pl");
}

export function init() {
	if (!document.body.classList.contains("id--17")) return;
	if (!isPlCheckout()) return;

	const form = document.querySelector("#order-form");
	if (!form) return;

	const billing = [
		form.querySelector("#billStreet"),
		form.querySelector("#billCity"),
		form.querySelector("#billZip"),
	];
	const delivery = [
		form.querySelector("#deliveryStreet"),
		form.querySelector("#deliveryCity"),
		form.querySelector("#deliveryZip"),
	];

	bindStreetField(billing[0], billing);
	bindCityField(billing[1], billing);
	bindZipField(billing[2], billing);

	bindStreetField(delivery[0], delivery);
	bindCityField(delivery[1], delivery);
	bindZipField(delivery[2], delivery);

	form.addEventListener("submit", onSubmitAttempt, true);

	const submitBtn = form.querySelector("#submit-order");
	if (submitBtn) {
		submitBtn.addEventListener("click", onSubmitAttempt, true);
	}

	function onSubmitAttempt(event) {
		if (validateCheckoutAddress(form)) return;
		event.preventDefault();
		event.stopPropagation();
		event.stopImmediatePropagation();
		scrollToFirstError(form);
	}
}

function validateCheckoutAddress(form) {
	let ok = true;

	ok = validateZip(form.querySelector("#billZip")) && ok;
	ok = validateStreet(form.querySelector("#billStreet")) && ok;
	clearStaleRequiredError(form.querySelector("#billCity"));

	if (isAnotherShippingEnabled(form)) {
		ok = validateZip(form.querySelector("#deliveryZip")) && ok;
		ok = validateStreet(form.querySelector("#deliveryStreet")) && ok;
		clearStaleRequiredError(form.querySelector("#deliveryCity"));
	} else {
		clearFieldError(form.querySelector("#deliveryZip"), ZIP_ERROR_TYPE);
		clearFieldError(form.querySelector("#deliveryStreet"), STREET_ERROR_TYPE);
	}

	return ok;
}

function isAnotherShippingEnabled(form) {
	const checkbox = form.querySelector("#another-shipping");
	return Boolean(checkbox && checkbox.checked);
}

function bindZipField(input, group) {
	if (!input) return;

	input.setAttribute("inputmode", "numeric");
	input.setAttribute("maxlength", "6");

	input.addEventListener("input", function () {
		const before = input.value;
		const cleaned = sanitizeZipInput(before);
		if (cleaned !== before) {
			const cursor = input.selectionStart;
			input.value = cleaned;
			if (typeof cursor === "number") {
				const pos = Math.min(cursor, cleaned.length);
				input.setSelectionRange(pos, pos);
			}
		}
		onAddressGroupTyping(group, input);
		if (!input.value.trim() || zipDigitCount(input.value) < 5) {
			clearFieldError(input, ZIP_ERROR_TYPE);
			return;
		}
		if (isValidPlZip(input.value)) {
			clearFieldError(input, ZIP_ERROR_TYPE);
			clearStaleRequiredError(input);
		}
	});

	input.addEventListener("blur", function () {
		validateZip(input);
		scheduleOrderedFoxentryNudge(group, input);
	});
}

function bindStreetField(input, group) {
	if (!input) return;

	input.addEventListener("blur", function () {
		validateStreet(input);
		scheduleOrderedFoxentryNudge(group, input);
	});
	input.addEventListener("input", function () {
		onAddressGroupTyping(group, input);
		if (!input.value.trim() || hasHouseNumber(input.value)) {
			clearFieldError(input, STREET_ERROR_TYPE);
			if (hasHouseNumber(input.value)) clearStaleRequiredError(input);
		}
	});
}

function bindCityField(input, group) {
	if (!input) return;
	input.addEventListener("blur", function () {
		clearStaleRequiredError(input);
		scheduleOrderedFoxentryNudge(group, input);
	});
	input.addEventListener("input", function () {
		onAddressGroupTyping(group, input);
		clearStaleRequiredError(input);
	});
	input.addEventListener("change", function () {
		onAddressGroupTyping(group, input);
		clearStaleRequiredError(input);
		scheduleOrderedFoxentryNudge(group, input);
	});
}

function onAddressGroupTyping(group, sourceInput) {
	if (!group || !group.length) return;
	group.forEach(function (field) {
		if (!field || field === sourceInput) return;
		clearStaleRequiredError(field);
		if (field.id === "billZip" || field.id === "deliveryZip") {
			if (!field.value.trim() || isValidPlZip(field.value)) {
				clearFieldError(field, ZIP_ERROR_TYPE);
			}
			return;
		}
		if (field.id === "billStreet" || field.id === "deliveryStreet") {
			if (!field.value.trim() || hasHouseNumber(field.value)) {
				clearFieldError(field, STREET_ERROR_TYPE);
			}
		}
	});
}

// Foxentry validates the address as one group. Re-check in docs order:
// street (+ house number) → city → ZIP. Never fire all siblings at once.
function scheduleOrderedFoxentryNudge(group, sourceInput) {
	if (!group || !group.length) return;
	if (!isAddressGroupLocallyComplete(group)) return;

	clearTimeout(group._natimaFoxentryTimer);
	group._natimaFoxentryTimer = setTimeout(function () {
		runOrderedFoxentryNudge(group, sourceInput);
	}, 250);
}

function isAddressGroupLocallyComplete(group) {
	const street = group[0];
	const city = group[1];
	const zip = group[2];
	if (!hasInputValue(street) || !hasHouseNumber(street.value)) return false;
	if (!hasInputValue(city)) return false;
	if (!hasInputValue(zip) || !isValidPlZip(zip.value)) return false;
	return true;
}

function runOrderedFoxentryNudge(group, sourceInput) {
	if (group._natimaFoxentryRunning) return;
	group._natimaFoxentryRunning = true;

	const steps = group.filter(Boolean);
	let index = 0;

	function step() {
		if (index >= steps.length) {
			group._natimaFoxentryRunning = false;
			return;
		}

		const field = steps[index++];
		// Skip re-firing the field the user just left — Foxentry already saw it.
		if (field === sourceInput) {
			step();
			return;
		}

		try {
			field.dispatchEvent(new Event("input", { bubbles: true }));
			field.dispatchEvent(new Event("change", { bubbles: true }));
			field.dispatchEvent(new FocusEvent("blur", { bubbles: true }));
		} catch (error) {
			// ignore
		}

		setTimeout(step, FOXENTRY_STEP_MS);
	}

	step();
}

// Keep digits and an existing hyphen from Foxentry/user — never insert "-".
function sanitizeZipInput(raw) {
	return String(raw || "").replace(/[^\d-]/g, "").slice(0, 6);
}

function zipDigitCount(value) {
	return String(value || "").replace(/\D/g, "").length;
}

function isValidPlZip(value) {
	return zipDigitCount(value) === 5;
}

function hasHouseNumber(value) {
	return /\d/.test(String(value || "").trim());
}

function hasInputValue(input) {
	return Boolean(input && String(input.value || "").trim());
}

function validateZip(input) {
	if (!input) return true;
	const sanitized = sanitizeZipInput(input.value);
	if (input.value !== sanitized) input.value = sanitized;
	const value = String(input.value || "").trim();

	if (!value) {
		clearFieldError(input, ZIP_ERROR_TYPE);
		return true;
	}

	if (!isValidPlZip(value)) {
		showFieldError(input, ZIP_ERROR_TYPE, window.NAT.translations(ZIP_MSG));
		return false;
	}

	clearFieldError(input, ZIP_ERROR_TYPE);
	clearStaleRequiredError(input);
	return true;
}

function validateStreet(input) {
	if (!input) return true;
	const value = String(input.value || "").trim();

	if (!value) {
		clearFieldError(input, STREET_ERROR_TYPE);
		return true;
	}

	if (!hasHouseNumber(value)) {
		showFieldError(input, STREET_ERROR_TYPE, window.NAT.translations(STREET_MSG));
		return false;
	}

	clearFieldError(input, STREET_ERROR_TYPE);
	clearStaleRequiredError(input);
	return true;
}

function clearStaleRequiredError(input) {
	if (!hasInputValue(input)) return;

	try {
		if (window.shoptet && shoptet.validator && typeof shoptet.validator.removeErrorMessage === "function") {
			shoptet.validator.removeErrorMessage(input, "validatorRequired");
		}
	} catch (error) {
		// ignore
	}

	const wrapper = input.closest(".js-validated-element-wrapper");
	if (wrapper) {
		wrapper.querySelectorAll('.js-validator-msg[data-type="validatorRequired"]').forEach(function (node) {
			node.remove();
		});
	}

	if (!wrapper || !wrapper.querySelector(".js-validator-msg.msg-error")) {
		input.classList.remove("js-error-field");
	}
}

function showFieldError(input, messageType, text) {
	const wrapper = input.closest(".js-validated-element-wrapper");
	if (!wrapper) return;

	clearFieldError(input, messageType);
	input.classList.add("js-error-field");

	const message = document.createElement("div");
	message.className = "js-validator-msg msg-error";
	message.dataset.type = messageType;
	message.textContent = text;
	wrapper.appendChild(message);
}

function clearFieldError(input, messageType) {
	if (!input) return;
	const wrapper = input.closest(".js-validated-element-wrapper");
	if (!wrapper) {
		input.classList.remove("js-error-field");
		return;
	}

	wrapper.querySelectorAll('.js-validator-msg[data-type="' + messageType + '"]').forEach(function (node) {
		node.remove();
	});

	if (!wrapper.querySelector(".js-validator-msg.msg-error")) {
		input.classList.remove("js-error-field");
	}
}

function scrollToFirstError(form) {
	const first = form.querySelector(".js-error-field");
	if (!first) return;
	const group = first.closest(".form-group") || first;
	group.scrollIntoView({ behavior: "smooth", block: "center" });
	try {
		first.focus({ preventScroll: true });
	} catch (error) {
		first.focus();
	}
}
